<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Checkbox, Toggle, Input, Text } from '@Shelly/shelly-design-system';

	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { conditionsFunc, getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
		import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { format } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	$: settingsStore = getDeviceSettings(device?.id);
	export let device: Device;

	// {
	// 	close_time: 1.5;
	// 	enable: true;
	// 	open_time: 1.4;
	// 	precise_ctl: false;
	// 	retain_pos: true;
	// 	step: 20;
	// }

	const setting = $settingsStore[`cover:${device.channel}`]?.slat;
	let enabled = setting?.enable;
	let open_time = setting?.open_time;
	let close_time = setting?.close_time;
	let precise_ctl = setting?.precise_ctl;
	let retain_pos = setting?.retain_pos;
	let step = setting?.step;
	let loading = false;

	const setSlat = async () => {
		loading = true;
		const params = {
			id: device.channel,
			config: {
				slat: {
					enable: enabled,
					open_time: open_time,
					close_time: close_time,
					step: step,
					precise_ctl: precise_ctl,
					retain_pos: retain_pos,
				},
			},
		};
		const res: SettingRequest = await gen2Request(device, 'cover.setconfig', params);
		onSettingsResponse(res, device);
		loading = false;
	};

	let openAndCloseTime = {
		min: 0,
		max: 10,
	};

	let stepTime = {
		min: 0,
		max: 100,
	};

	$: openTimeMinAndMaxError = getErrorOrInfoMessageForMinAndMax(
		open_time,
		openAndCloseTime.min,
		openAndCloseTime.max,
	).error;

	$: closeTimeMinAndMaxError = getErrorOrInfoMessageForMinAndMax(
		close_time,
		openAndCloseTime.min,
		openAndCloseTime.max,
	).error;

	$: stepTimeMinAndMaxError = getErrorOrInfoMessageForMinAndMax(
		step,
		stepTime.min,
		stepTime.max,
	).error;

	$: stepTimeDecimalError = conditionsFunc(step) ? $format('scene_invalid_format_err_toast') : '';

	$: stepTimeError = stepTimeMinAndMaxError || stepTimeDecimalError;

	$: tiltControlError = openTimeMinAndMaxError || closeTimeMinAndMaxError || stepTimeError;

	const onToggleChange = () => {
		enabled = !enabled;
		setSlat();
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && enabled) {
			stickyBottomAction.set({
				onClick: setSlat,
				loading,
				disabled: loading || !!tiltControlError,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Toggle class="mb-s-plus" size="m" checked={enabled} on:click={onToggleChange} id="checkbox-olikndi">
		{$format('label_enable', { default: 'Enable' })}
	</Toggle>
	{#if enabled}
		<Text class="mb-s" styleType="body-small">
			{$format('label_opening_time', {
				default: 'Opening time',
			})}
		</Text>

		<Input
			size="m"
			class="mb-s-plus"
			type="number"
			step={0.1}
			label={$format('label_opening_time', { default: 'Opening time' })}
			min={openAndCloseTime.min}
			max={openAndCloseTime.max}
			bind:value={open_time}
			error={openTimeMinAndMaxError}
			id="input-1c694y8"
			aria-labelledby="input-1c694y8"
		/>
		<Text class="mb-s" styleType="body-small">
			{$format('label_closing_time', {
				default: 'Closing time',
			})}
		</Text>

		<Input
			size="m"
			class="mb-s-plus"
			type={'number'}
			step={0.1}
			min={openAndCloseTime.min}
			max={openAndCloseTime.max}
			bind:value={close_time}
			error={closeTimeMinAndMaxError}
			id="input-c4zq9si"
			aria-label={$format('label_closing_time', {
				default: 'Closing time',
			})}
		/>
		<Text class="mb-s" styleType="body-small">
			{$format('label_step', {
				default: 'Step',
			})}
		</Text>

		<Input
			size="m"
			class="mb-s-plus"
			type={'number'}
			min={stepTime.min}
			max={stepTime.max}
			step={1}
			bind:value={step}
			error={stepTimeError || ''}
			id="input-gyioudp"
			aria-label={$format('label_step', { default: 'Step' })}
		/>

		<Checkbox size="m" bind:checked={retain_pos} id="checkbox-9twv42k">
			{$format('label_retain_tilt_position', {
				default: 'Retain tilt position',
			})}
		</Checkbox>

		<Checkbox class="mt-s" size="m" bind:checked={precise_ctl} id="checkbox-u3gxscb">
			{$format('label_precise_tilt_control', {
				default: 'Precise tilt control',
			})}
		</Checkbox>
	{/if}
{:else}
	<Checkbox class="mt-s" size="m" bind:checked={enabled} id="checkbox-olikndi"
		>{$format('label_enable', { default: 'Enable' })}</Checkbox
	>
	<Text class="mb-s" styleType="body-small">
		{$format('label_opening_time', {
			default: 'Opening time',
		})}
	</Text>

	<Input
		size="m"
		class="mb-s"
		type="number"
		step={0.1}
		label={$format('label_opening_time', { default: 'Opening time' })}
		min={openAndCloseTime.min}
		max={openAndCloseTime.max}
		bind:value={open_time}
		error={openTimeMinAndMaxError}
		id="input-1c694y8"
		aria-labelledby="input-1c694y8"
	/>
	<Text class="mb-s" styleType="body-small">
		{$format('label_closing_time', {
			default: 'Closing time',
		})}
	</Text>

	<Input
		size="m"
		class="mb-s"
		type={'number'}
		step={0.1}
		min={openAndCloseTime.min}
		max={openAndCloseTime.max}
		bind:value={close_time}
		error={closeTimeMinAndMaxError}
		id="input-c4zq9si"
		aria-label={$format('label_closing_time', {
			default: 'Closing time',
		})}
	/>
	<Text class="mb-s" styleType="body-small">
		{$format('label_step', {
			default: 'Step',
		})}
	</Text>

	<Input
		size="m"
		class="mb-s"
		type={'number'}
		min={stepTime.min}
		max={stepTime.max}
		step={1}
		bind:value={step}
		error={stepTimeError || ''}
		id="input-gyioudp"
		aria-label={$format('label_step', { default: 'Step' })}
	/>
	<Checkbox class="mt-s" size="m" bind:checked={retain_pos} id="checkbox-9twv42k"
		>{$format('label_retain_tilt_position', {
			default: 'Retain tilt position',
		})}</Checkbox
	>

	<Checkbox class="mt-s" size="m" bind:checked={precise_ctl} id="checkbox-u3gxscb"
		>{$format('label_precise_tilt_control', {
			default: 'Precise tilt control',
		})}</Checkbox
	>

	<Button
		size="m"
		width="full"
		disabled={loading || tiltControlError}
		on:click={!tiltControlError ? setSlat : undefined}
		{loading}>{$format('button_save', { default: 'Save' })}</Button
	>
{/if}
